import { AlertTriangle, ChevronRight, FolderPlus, Layers, Search, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { EmptyState, ErrorState, LoadingState } from "@/components/feedback";
import { Button } from "@/components/ui/button";
import {
  CATEGORY_LABEL,
  type FolderItemOut,
  type FolderOut,
  type NoteCategory,
  type SharedFolderOut,
} from "@/lib/api";
import { CATEGORY_COLOR, folderColor } from "@/lib/palette";
import { cn } from "@/lib/utils";

import { FileContextMenu } from "./FileContextMenu";
import { FILE_DND_TYPE, FileTree } from "./FileTree";
import { openFile } from "./file-actions";
import { useFileReveal } from "@/stores/file-reveal";

import { applyClick, refKey, toFileRef, useFileSelection, type FileRef } from "./file-selection";
import { useFileManager } from "./use-file-manager";
import { useFolderUpload } from "./use-folder-upload";
import { canMoveInto, movingRefs, type MoveTarget } from "./use-move-items";

/** What the right-hand pane is showing: a folder's subfolders and its files,
 * plus enough about each subfolder to draw a row and accept a drop. A PROJECT
 * folder and a category folder differ only in whether a drop carries a
 * destination category, so one shape covers both. */
interface Contents {
  path: string;
  total_count: number;
  folders: { name: string; path: string; color: string | null; count: number;
             category: NoteCategory | null }[];
  files: FolderItemOut[];
}

/** Depth-first search for a folder by category + path. */
function findFolder(roots: FolderOut[], category: NoteCategory, path: string): FolderOut | null {
  const walk = (folder: FolderOut): FolderOut | null => {
    if (folder.category === category && folder.path === path) return folder;
    for (const child of folder.children ?? []) {
      const hit = walk(child);
      if (hit) return hit;
    }
    return null;
  };
  for (const root of roots) {
    const hit = walk(root);
    if (hit) return hit;
  }
  return null;
}

function findProject(shared: SharedFolderOut[], path: string): SharedFolderOut | null {
  for (const project of shared) {
    if (project.path === path) return project;
    const hit = findProject(project.children ?? [], path);
    if (hit) return hit;
  }
  return null;
}

/** A project's contents are the UNION of its facets — the same files the
 * category folders show, gathered. Nothing is copied to make that true. */
function projectContents(project: SharedFolderOut): Contents {
  return {
    path: project.path,
    total_count: project.total_count,
    folders: (project.children ?? []).map((child) => ({
      name: child.name,
      path: child.path,
      color: child.color,
      count: child.total_count,
      category: null,
    })),
    files: (project.facets ?? []).flatMap((facet) => facet.items ?? []),
  };
}

function folderContents(folder: FolderOut): Contents {
  return {
    path: folder.path,
    total_count: folder.total_count,
    folders: (folder.children ?? []).map((child) => ({
      name: child.name,
      path: child.path,
      color: child.color,
      count: child.total_count,
      category: child.category,
    })),
    files: folder.items ?? [],
  };
}

/**
 * Folder View — a conventional two-pane file explorer over the vault.
 *
 * Deliberately NOT another grid of cards: the homepage already is one, and the
 * brief asks for the thing a grid can't do — see where something lives, move it
 * somewhere else, and act on several files at once. So: a folder tree on the
 * left, the contents of the selected folder on the right, a breadcrumb above it,
 * and every gesture a file explorer is expected to have.
 */
export function FolderView() {
  const manager = useFileManager();
  const { roots, tree } = manager;
  const shared = useMemo(() => manager.tree.data?.shared ?? [], [manager.tree.data]);
  // A null category means a PROJECT is open — the same folder seen across every
  // category at once, rather than one category's slice of it.
  const [current, setCurrent] = useState<MoveTarget>({ category: "markdown", folder: "" });
  const [query, setQuery] = useState("");
  const contents = useMemo((): Contents | null => {
    if (current.category === null) {
      const project = findProject(shared, current.folder);
      return project ? projectContents(project) : null;
    }
    const folder = findFolder(roots, current.category, current.folder);
    return folder ? folderContents(folder) : null;
  }, [roots, shared, current]);

  // A folder that was renamed, deleted or emptied out from under the user (by
  // this pane or another surface) must not leave the right pane blank forever.
  useEffect(() => {
    if (roots.length && !contents) {
      setCurrent({ category: current.category ?? "markdown", folder: "" });
    }
  }, [roots, contents, current.category]);

  if (tree.isLoading) return <LoadingState variant="skeleton" rows={8} className="p-4" />;
  if (tree.isError) {
    return (
      <ErrorState
        error={tree.error}
        onRetry={() => void tree.refetch()}
        label="folders"
        className="p-4"
      />
    );
  }

  return (
    <div className="flex h-full w-full bg-background">
      {/* ── tree ─────────────────────────────────────────────────────────── */}
      <div className="flex w-[248px] shrink-0 flex-col border-r border-border">
        <div className="flex h-10 shrink-0 items-center gap-1 border-b border-border px-2">
          <span className="flex-1 pl-1 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
            Folders
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            aria-label="New folder"
            title="New folder"
            onClick={() =>
              manager.setEditing({
                kind: "new-folder",
                category: current.category,
                parent: current.folder,
              })
            }
          >
            <FolderPlus className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
          <FileTree
            roots={roots}
            shared={shared}
            defaultOpenDepth={1}
            callbacks={{
              onOpen: manager.openItem,
              onDropInto: manager.dropInto,
              onFolderContextMenu: (f, at) => manager.setMenu({ kind: "folder", folder: f, ...at }),
              onItemContextMenu: (refs, at) => manager.setMenu({ kind: "files", refs, ...at }),
              sharedActions: (f) => (
                <button
                  type="button"
                  aria-label={`Open project ${f.name}`}
                  title="Show everything in this project"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrent({ category: null, folder: f.path });
                  }}
                  className={cn(
                    "rounded p-0.5 text-muted-foreground opacity-0 transition-opacity",
                    "hover:bg-accent hover:text-foreground focus-visible:opacity-100 group-hover/folder:opacity-100",
                  )}
                >
                  <ChevronRight className="h-3 w-3" />
                </button>
              ),
              folderActions: (f) => (
                <button
                  type="button"
                  aria-label={`Open ${f.name}`}
                  title="Show contents"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrent({ category: f.category, folder: f.path });
                  }}
                  className={cn(
                    "rounded p-0.5 text-muted-foreground opacity-0 transition-opacity",
                    "hover:bg-accent hover:text-foreground focus-visible:opacity-100 group-hover/folder:opacity-100",
                  )}
                >
                  <ChevronRight className="h-3 w-3" />
                </button>
              ),
            }}
          />
          {manager.editing?.kind === "new-folder" && (
            <InlineInput
              placeholder={`New folder in ${
                manager.editing.parent ||
                (manager.editing.category ? CATEGORY_LABEL[manager.editing.category] : "Folders")
              }…`}
              onCommit={manager.commitEditing}
              onCancel={() => manager.setEditing(null)}
            />
          )}
        </div>
      </div>

      {/* ── contents ─────────────────────────────────────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col">
        <Breadcrumb current={current} contents={contents} onNavigate={setCurrent} />
        <div className="flex h-10 shrink-0 items-center gap-2 border-b border-border px-3">
          <div className="relative flex min-w-0 flex-1 items-center">
            <Search className="pointer-events-none absolute left-2.5 h-3 w-3 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter this folder…"
              aria-label="Filter this folder"
              className="h-7 w-full rounded border border-border bg-input pl-7 pr-2 text-[12px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <SelectionBar onDelete={(refs) => manager.setConfirmDelete(refs)} />
        </div>
        <FolderContents
          current={current}
          contents={contents}
          query={query}
          onNavigate={setCurrent}
          onDropInto={manager.dropInto}
          onContextMenu={(refs, at) => manager.setMenu({ kind: "files", refs, ...at })}
          onFolderContextMenu={(f, at) => manager.setMenu({ kind: "folder", folder: f, ...at })}
        />
      </div>

      <FileContextMenu
        state={manager.menu}
        onClose={() => manager.setMenu(null)}
        canPaste={!!manager.clipboard}
        onRenameFolder={manager.folderActions.rename}
        onNewSubfolder={manager.folderActions.newSubfolder}
        onSetColor={manager.folderActions.setColor}
        onDeleteFolder={manager.folderActions.remove}
        onPaste={manager.paste}
        onRenameFile={(ref) => manager.setEditing({ kind: "rename-file", ref })}
        onDeleteFiles={(refs) => manager.setConfirmDelete(refs)}
        onCut={(refs) => useFileSelection.getState().cut(refs)}
        onCopy={(refs) => useFileSelection.getState().copy(refs)}
        onDuplicate={manager.duplicate}
      />

      {manager.editing?.kind === "rename-file" && (
        <RenameDialog
          title={manager.editing.ref.title}
          onCommit={manager.commitEditing}
          onCancel={() => manager.setEditing(null)}
        />
      )}
      {manager.editing?.kind === "rename-folder" && (
        <RenameDialog
          title={manager.editing.folder.name}
          onCommit={manager.commitEditing}
          onCancel={() => manager.setEditing(null)}
        />
      )}
      {manager.confirmDelete && (
        <ConfirmDelete
          refs={manager.confirmDelete}
          onCancel={() => manager.setConfirmDelete(null)}
          onConfirm={() => void manager.deleteSelected(manager.confirmDelete!)}
        />
      )}
    </div>
  );
}

function Breadcrumb({
  current,
  contents,
  onNavigate,
}: {
  current: MoveTarget;
  contents: Contents | null;
  onNavigate: (target: MoveTarget) => void;
}) {
  const segments = current.folder ? current.folder.split("/") : [];
  return (
    <div className="flex h-10 shrink-0 items-center gap-1 border-b border-border px-3 text-[12.5px]">
      {current.category === null ? (
        <Layers aria-hidden className="h-3 w-3 shrink-0 text-muted-foreground" />
      ) : (
        <span
          aria-hidden
          className="h-[7px] w-[7px] shrink-0 rounded-[2px]"
          style={{ background: CATEGORY_COLOR[current.category] ?? "" }}
        />
      )}
      <button
        type="button"
        onClick={() => onNavigate({ category: current.category, folder: "" })}
        className="rounded px-1 text-muted-foreground transition-colors hover:text-foreground"
      >
        {current.category === null ? "Folders" : CATEGORY_LABEL[current.category]}
      </button>
      {segments.map((segment, i) => (
        <span key={segment + i} className="flex min-w-0 items-center gap-1">
          <span className="text-muted-foreground/40">/</span>
          <button
            type="button"
            onClick={() =>
              onNavigate({ category: current.category, folder: segments.slice(0, i + 1).join("/") })
            }
            className={cn(
              "truncate rounded px-1 transition-colors hover:text-foreground",
              i === segments.length - 1 ? "text-foreground" : "text-muted-foreground",
            )}
          >
            {segment}
          </button>
        </span>
      ))}
      {contents && (
        <span className="ml-auto shrink-0 font-mono text-[10px] text-muted-foreground">
          {contents.total_count} item{contents.total_count === 1 ? "" : "s"}
        </span>
      )}
    </div>
  );
}

/** The right pane: subfolders then files, both selectable and draggable, with a
 * marquee for "select a run of things" that clicking one at a time can't do. */
function FolderContents({
  current,
  contents,
  query,
  onNavigate,
  onDropInto,
  onContextMenu,
  onFolderContextMenu,
}: {
  current: MoveTarget;
  contents: Contents | null;
  query: string;
  onNavigate: (target: MoveTarget) => void;
  onDropInto: (refs: FileRef[], target: MoveTarget) => void;
  onContextMenu: (refs: FileRef[], at: { x: number; y: number }) => void;
  onFolderContextMenu: (folder: FolderOut, at: { x: number; y: number }) => void;
}) {
  const surfaceRef = useRef<HTMLDivElement>(null);
  const [marquee, setMarquee] = useState<{ x: number; y: number; w: number; h: number } | null>(
    null,
  );
  const needle = query.trim().toLowerCase();
  const folders = (contents?.folders ?? []).filter((c) => c.name.toLowerCase().includes(needle));
  const files = (contents?.files ?? []).filter((i) => i.title.toLowerCase().includes(needle));
  const ordered = useMemo(
    () => files.map((item) => toFileRef(item, contents?.path ?? "")),
    [files, contents],
  );
  // Dropping OS files on the empty space of an open folder uploads them HERE,
  // which is what the whitespace of any file explorer means.
  const native = useFolderUpload({
    folder: current.folder,
    label: current.folder || "your vault",
    category: current.category,
  });

  // Marquee selection. Pointer events on the surface, hit-tested against the
  // rows' own rects — no library, because the whole behaviour is "which boxes
  // does this box touch".
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0 || e.target !== e.currentTarget) return;
    const host = surfaceRef.current;
    if (!host) return;
    const origin = { x: e.clientX, y: e.clientY };
    if (!e.ctrlKey && !e.metaKey) useFileSelection.getState().clear();
    const base = new Set(useFileSelection.getState().selected.keys());

    const onMove = (ev: PointerEvent) => {
      const box = {
        x: Math.min(origin.x, ev.clientX),
        y: Math.min(origin.y, ev.clientY),
        w: Math.abs(ev.clientX - origin.x),
        h: Math.abs(ev.clientY - origin.y),
      };
      if (box.w < 4 && box.h < 4) return; // a click, not a drag
      setMarquee(box);
      const hits: FileRef[] = [];
      for (const [index, node] of [...host.querySelectorAll("[data-file-row]")].entries()) {
        const r = node.getBoundingClientRect();
        const overlaps =
          r.left < box.x + box.w && r.right > box.x && r.top < box.y + box.h && r.bottom > box.y;
        const ref = ordered[index];
        if (ref && (overlaps || base.has(refKey(ref)))) hits.push(ref);
      }
      useFileSelection.getState().setSelection(hits, null);
    };
    const onUp = () => {
      setMarquee(null);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  if (!contents) {
    return <EmptyState icon={Search} title="Pick a folder" className="pt-16" />;
  }

  return (
    <div
      ref={surfaceRef}
      onPointerDown={onPointerDown}
      {...native.props}
      // A flat, multi-selectable list of rows — so these ARE options, and now
      // they have the listbox an `option` requires to mean anything.
      role="listbox"
      aria-label="Folder contents"
      aria-multiselectable="true"
      className={cn(
        "relative min-h-0 flex-1 overflow-y-auto p-2",
        native.over && "ring-1 ring-inset ring-primary/50",
      )}
    >
      {folders.length === 0 && files.length === 0 && (
        <EmptyState
          icon={Search}
          title={needle ? "Nothing matches" : "This folder is empty"}
          description={
            needle ? undefined : "Drag notes or files from your desktop here."
          }
          className="pt-14"
        />
      )}

      {folders.map((child) => (
        <FolderRow
          key={`${child.category ?? "project"}:${child.path}`}
          entry={child}
          onNavigate={onNavigate}
          onDropInto={onDropInto}
          onContextMenu={onFolderContextMenu}
        />
      ))}

      {files.map((item, index) => {
        const ref = ordered[index]!;
        return (
          <ContentFileRow
            key={refKey(ref)}
            item={item}
            fileRef={ref}
            ordered={ordered}
            onContextMenu={onContextMenu}
          />
        );
      })}

      {marquee && (
        <div
          className="pointer-events-none fixed z-50 rounded-sm border border-primary/60 bg-primary/15"
          style={{ left: marquee.x, top: marquee.y, width: marquee.w, height: marquee.h }}
        />
      )}
    </div>
  );
}

function FolderRow({
  entry,
  onNavigate,
  onDropInto,
  onContextMenu,
}: {
  entry: Contents["folders"][number];
  onNavigate: (target: MoveTarget) => void;
  onDropInto: (refs: FileRef[], target: MoveTarget) => void;
  onContextMenu: (folder: FolderOut, at: { x: number; y: number }) => void;
}) {
  const [hover, setHover] = useState<"none" | "ok" | "blocked">("none");
  const target: MoveTarget = { category: entry.category, folder: entry.path };
  const native = useFolderUpload({
    folder: entry.path,
    label: entry.name,
    category: entry.category,
  });
  return (
    <button
      type="button"
      onDoubleClick={() => onNavigate(target)}
      onClick={() => onNavigate(target)}
      // The same menu the tree offers. A folder shown in two places that only
      // answers a right-click in one of them is the kind of inconsistency that
      // makes people stop trying. A PROJECT row has no single category folder
      // behind it, so it opens instead of offering per-category actions.
      onContextMenu={(e) => {
        if (entry.category === null) return;
        e.preventDefault();
        onContextMenu(
          {
            id: null,
            category: entry.category,
            path: entry.path,
            name: entry.name,
            color: entry.color,
            total_count: entry.count,
            children: [],
            items: [],
          } as unknown as FolderOut,
          { x: e.clientX, y: e.clientY },
        );
      }}
      onDragEnter={(e) => {
        native.props.onDragEnter(e);
        if (!e.dataTransfer.types.includes(FILE_DND_TYPE)) return;
        try {
          const refs = JSON.parse(e.dataTransfer.getData(FILE_DND_TYPE) || "[]") as FileRef[];
          setHover(refs.length === 0 || canMoveInto(refs, target) ? "ok" : "blocked");
        } catch {
          setHover("ok");
        }
      }}
      onDragOver={(e) => {
        native.props.onDragOver(e);
        if (!e.dataTransfer.types.includes(FILE_DND_TYPE)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = hover === "blocked" ? "none" : "move";
      }}
      onDragLeave={(e) => {
        native.props.onDragLeave(e);
        setHover("none");
      }}
      onDrop={(e) => {
        native.props.onDrop(e);
        if (!e.dataTransfer.types.includes(FILE_DND_TYPE)) return;
        e.preventDefault();
        setHover("none");
        try {
          const refs = JSON.parse(e.dataTransfer.getData(FILE_DND_TYPE) || "[]") as FileRef[];
          const moving = movingRefs(refs, target);
          if (moving.length) onDropInto(moving, target);
        } catch {
          /* a malformed payload is not a move */
        }
      }}
      className={cn(
        "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left transition-colors",
        (hover === "ok" || native.over) && "bg-primary/20 ring-1 ring-primary/50",
        hover === "blocked" && "bg-destructive/10 ring-1 ring-destructive/40",
        hover === "none" && !native.over && "hover:bg-accent/60",
      )}
    >
      {entry.category === null ? (
        <Layers className="h-4 w-4 shrink-0" style={{ color: folderColor(entry.color) }} />
      ) : (
        <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" style={{ color: folderColor(entry.color) }}>
          <path
            fill="currentColor"
            d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"
            opacity="0.85"
          />
        </svg>
      )}
      <span className="truncate text-[12.5px] text-foreground/85">{entry.name}</span>
      <span className="ml-auto shrink-0 font-mono text-[9px] text-muted-foreground/70">
        {entry.count || ""}
      </span>
    </button>
  );
}

function ContentFileRow({
  item,
  fileRef,
  ordered,
  onContextMenu,
}: {
  item: FolderItemOut;
  fileRef: FileRef;
  ordered: FileRef[];
  onContextMenu: (refs: FileRef[], at: { x: number; y: number }) => void;
}) {
  const selected = useFileSelection((s) => s.selected.has(refKey(fileRef)));
  const cut = useFileSelection(
    (s) =>
      s.clipboard?.mode === "cut" && s.clipboard.refs.some((r) => refKey(r) === refKey(fileRef)),
  );

  // Arriving from "Show this file in Folder View": select it, scroll it into
  // view and flash it. A pane that merely opens leaves you hunting for the row,
  // which is what made that button annoying rather than useful.
  const revealId = useFileReveal((s) => s.id);
  const revealAt = useFileReveal((s) => s.at);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const [flash, setFlash] = useState(false);
  const isTarget = !!revealId && (revealId === item.id || revealId === fileRef.nodeId);

  useEffect(() => {
    if (!isTarget || !rowRef.current) return;
    useFileSelection.getState().select(fileRef);
    rowRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
    setFlash(true);
    const done = window.setTimeout(() => {
      setFlash(false);
      useFileReveal.getState().clearReveal();
    }, 1600);
    return () => window.clearTimeout(done);
    // `revealAt` is the trigger: asking for the same file twice must flash again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTarget, revealAt]);

  return (
    <div
      ref={rowRef}
      data-file-row
      role="option"
      aria-selected={selected}
      draggable
      onDragStart={(e) => {
        const store = useFileSelection.getState();
        if (!store.isSelected(fileRef)) store.select(fileRef);
        e.dataTransfer.setData(
          FILE_DND_TYPE,
          JSON.stringify(store.isSelected(fileRef) ? store.selectedRefs() : [fileRef]),
        );
        e.dataTransfer.effectAllowed = "move";
      }}
      onClick={(e) => {
        e.stopPropagation();
        applyClick(fileRef, ordered, { ctrl: e.ctrlKey || e.metaKey, shift: e.shiftKey });
      }}
      onDoubleClick={() => openFile(item)}
      onContextMenu={(e) => {
        e.preventDefault();
        const store = useFileSelection.getState();
        if (!store.isSelected(fileRef)) store.select(fileRef);
        onContextMenu(store.selectedRefs(), { x: e.clientX, y: e.clientY });
      }}
      className={cn(
        flash && "animate-pulse ring-2 ring-primary ring-offset-2 ring-offset-background",
        "flex cursor-default items-center gap-2 rounded px-2 py-1.5 transition-colors",
        selected ? "bg-primary/20 text-foreground" : "text-foreground/75 hover:bg-accent/60",
        cut && "opacity-45",
      )}
    >
      <span className="truncate text-[12.5px]">{item.title}</span>
      {item.status === "failed" && (
        <span className="ml-auto shrink-0 font-mono text-[9px] uppercase text-destructive/80">
          failed
        </span>
      )}
    </div>
  );
}

/** Shown only while something is selected — the count, and the actions that only
 * make sense for a multi-selection. */
function SelectionBar({ onDelete }: { onDelete: (refs: FileRef[]) => void }) {
  const count = useFileSelection((s) => s.selected.size);
  if (count === 0) return null;
  return (
    <div className="flex shrink-0 items-center gap-1.5">
      <span className="font-mono text-[10px] text-muted-foreground">{count} selected</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-6 w-6 text-destructive/80 hover:text-destructive"
        aria-label={`Delete ${count} selected`}
        onClick={() => onDelete(useFileSelection.getState().selectedRefs())}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-6 w-6"
        aria-label="Clear selection"
        onClick={() => useFileSelection.getState().clear()}
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function InlineInput({
  placeholder,
  onCommit,
  onCancel,
}: {
  placeholder: string;
  onCommit: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState("");
  return (
    <input
      autoFocus
      value={value}
      placeholder={placeholder}
      onChange={(e) => setValue(e.target.value)}
      onBlur={onCancel}
      onKeyDown={(e) => {
        if (e.key === "Enter") onCommit(value);
        if (e.key === "Escape") onCancel();
      }}
      className="mt-1 w-full rounded border border-border bg-input px-2 py-1 text-[12px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
    />
  );
}

function RenameDialog({
  title,
  onCommit,
  onCancel,
}: {
  title: string;
  onCommit: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(title);
  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-black/50" onClick={onCancel}>
      <div
        className="w-[340px] rounded-lg bg-popover p-4 shadow-[0_24px_64px_-24px_rgba(0,0,0,0.9)]"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="pb-2 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
          Rename
        </p>
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onCommit(value);
            if (e.key === "Escape") onCancel();
          }}
          onFocus={(e) => e.currentTarget.select()}
          className="w-full rounded border border-border bg-input px-2 py-1.5 text-[13px] text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        />
        <div className="mt-3 flex justify-end gap-1.5">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => onCommit(value)}>
            Rename
          </Button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDelete({
  refs,
  onConfirm,
  onCancel,
}: {
  refs: FileRef[];
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-black/50" onClick={onCancel}>
      <div
        className="w-[380px] rounded-lg bg-popover p-4 shadow-[0_24px_64px_-24px_rgba(0,0,0,0.9)]"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="flex items-start gap-2 text-[13px] leading-relaxed text-foreground/90">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          {refs.length === 1
            ? `Delete “${refs[0]!.title}”?`
            : `Delete ${refs.length} files?`}
        </p>
        <p className="pl-6 pt-1 text-[12px] text-muted-foreground">
          This removes them from the atlas and your vault. Documents can be re-uploaded.
        </p>
        <div className="mt-3 flex justify-end gap-1.5">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={onConfirm}
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}
